import React, { useState } from 'react'
import { Send, Copy, Download, Loader } from 'lucide-react'
import { getToken } from '../auth.js'
import './AITool.css'

interface Field {
  name: string
  label: string
  type: 'text' | 'textarea' | 'select'
  placeholder?: string
  options?: string[]
}

interface AIToolProps {
  title: string
  description: string
  endpoint: string
  fields: Field[]
}

const AITool: React.FC<AIToolProps> = ({ title, description, endpoint, fields }) => {
  const [formData, setFormData] = useState<Record<string, string>>({})
  const [result, setResult] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleInputChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setResult('')

    try {
      const token = await getToken()
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      })

      if (!response.ok) {
        throw new Error('Failed to generate content')
      }

      const data = await response.json()
      setResult(data.result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(result)
  }

  const downloadResult = () => {
    const blob = new Blob([result], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${title.toLowerCase().replace(/\s+/g, '-')}-result.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="ai-tool">
      <div className="tool-header">
        <h2>{title}</h2>
        <p>{description}</p>
      </div>

      <div className="tool-container">
        <form onSubmit={handleSubmit} className="tool-form">
          {fields.map((field) => (
            <div key={field.name} className="form-group">
              <label htmlFor={field.name}>{field.label}</label>
              {field.type === 'textarea' ? (
                <textarea
                  id={field.name}
                  className="input textarea"
                  placeholder={field.placeholder}
                  value={formData[field.name] || ''}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                  required
                />
              ) : field.type === 'select' ? (
                <select
                  id={field.name}
                  className="input"
                  value={formData[field.name] || ''}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                  required
                >
                  <option value="">Select an option</option>
                  {field.options?.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id={field.name}
                  type="text"
                  className="input"
                  placeholder={field.placeholder}
                  value={formData[field.name] || ''}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                  required
                />
              )}
            </div>
          ))}

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <>
                <Loader className="loading" />
                Generating...
              </>
            ) : (
              <>
                <Send size={20} />
                Generate
              </>
            )}
          </button>
        </form>

        {error && (
          <div className="error">
            {error}
          </div>
        )}

        {result && (
          <div className="result-container">
            <div className="result-header">
              <h3>Generated Content</h3>
              <div className="result-actions">
                <button onClick={copyToClipboard} className="btn btn-secondary">
                  <Copy size={16} />
                  Copy
                </button>
                <button onClick={downloadResult} className="btn btn-secondary">
                  <Download size={16} />
                  Download
                </button>
              </div>
            </div>
            <div className="result-content">
              <pre>{result}</pre>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AITool 